<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { computed, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import IconClose from '@/components/icons/IconClose.vue';
import IconSearch from '@/components/icons/IconSearch.vue';
import Avatar from '@/components/ui/Avatar.vue';
import Input from '@/components/ui/Input.vue';
import { useThreadStore } from '@/stores/thread';
import type { SelectedSearchParam } from '@/types/thread';
import { useOnClickOutsideHide } from '@/utils/mixins';

interface SearchParamsProp {
  creator?: {
    uuid: string
    name: string
  }
}

interface Props {
  placeholder: string
  params?: SearchParamsProp | null
}

const props = defineProps<Props>();

const emit = defineEmits<(e: 'changed', value: SelectedSearchParam | null) => void>();

const { t } = useI18n();
const threadStore = useThreadStore();

const { searchCreators, searchUsers, searchParams } = toRefs(threadStore);
const { suggesterRef, isSuggesterOpen } = useOnClickOutsideHide();

const searchPhrase = ref('');
const selectedSearchValue = ref<SelectedSearchParam | null>(null);

const placeholder = computed(() => {
  if (selectedSearchValue.value?.creator) {
    return t('search.placeholder.to', { name: selectedSearchValue.value.creator.name });
  }

  if (selectedSearchValue.value?.sender) {
    return t('search.placeholder.from', { name: selectedSearchValue.value.sender.name });
  }

  if (selectedSearchValue.value?.searchPhrase) {
    return selectedSearchValue.value.searchPhrase;
  }

  return props.placeholder;
});

const emitSearchParam = (params: SelectedSearchParam | null) => {
  selectedSearchValue.value = params;
  isSuggesterOpen.value = false;
  searchPhrase.value = '';
  emit('changed', params);
};

const clearSearch = () => {
  searchPhrase.value = '';
  emitSearchParam(null);
};

const searchInputChanged = useDebounceFn(async () => {
  isSuggesterOpen.value = !!searchPhrase.value;
  await threadStore.getSearchSuggestions(searchPhrase.value);
}, 350);

onMounted(() => {
  let params: SelectedSearchParam | null = null;

  if (props.params?.creator) {
    searchPhrase.value = props.params.creator.name || '';
    params = {
      creator: {
        uuid: props.params.creator.uuid,
        name: props.params.creator.name
      }
    };
  }

  emitSearchParam(params);
});
</script>

<template>
  <div class="search-wrapper flex-center">
    <Input
      v-model="searchPhrase"
      input-id="search-input"
      name="search-input"
      type="text"
      :placeholder="placeholder"
      width="300px"
      close-icon
      @input="searchInputChanged()"
    >
      <template #icon>
        <IconSearch />
      </template>
    </Input>
    <span
      v-if="searchParams"
      class="clear-search"
      @click="clearSearch"
    >
      <IconClose />
    </span>
    <div
      v-if="isSuggesterOpen"
      ref="suggesterRef"
      class="search-suggester"
    >
      <p class="search-group">
        {{ $t('search.groups.users') }}
      </p>
      <template v-if="searchUsers.length">
        <div
          v-for="user in searchUsers"
          :key="user.name"
          class="search-item"
          @click="emitSearchParam({ sender: user })"
        >
          {{ user.name }}
        </div>
      </template>
      <div
        v-else
        class="search-item no-results"
      >
        {{ $t('search.noResults') }}
      </div>
      <p class="search-group">
        {{ $t('search.groups.influencers') }}
      </p>
      <template v-if="searchCreators.length">
        <div
          v-for="creator in searchCreators"
          :key="creator.name"
          class="search-item"
          @click="emitSearchParam({ creator })"
        >
          <Avatar
            :avatar-url="creator.avatarUrl"
            :name="creator.name"
            search
          />
        </div>
      </template>
      <div
        v-else
        class="search-item no-results"
      >
        {{ $t('search.noResults') }}
      </div>
      <div
        v-if="searchPhrase"
        class="search-item"
        @click="emitSearchParam({ searchPhrase })"
      >
        {{ $t('search.allSearchResultsFor') }} "{{ searchPhrase }}"
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.search-group {
  padding: 16px 14px 6px;
  color: var(--gray-darkest);
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  border-top: 1px solid var(--border-default);

  &:first-of-type {
    border-top: none;
  }
}

.search-wrapper {
  position: relative;
  margin: 0 auto 0 16px;
}

.search-suggester {
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  width: 300px;
  max-height: 450px;
  overflow: auto;
  background-color: var(--white);
  border: 1px solid var(--border-default);
  border-radius: 4px;
  z-index: 2;
}

.search-item {
  padding: 12px 14px;
  cursor: pointer;

  &.no-results {
    cursor: default;
  }

  &:hover {
    background-color: var(--gray);
  }
}

.clear-search {
   margin-left: -25px;
   font-size: 12px;
   font-weight: 500;
   text-transform: uppercase;
   cursor: pointer;
   z-index: 1;

   svg {
     max-width: 16px;
   }
 }
</style>
